import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { format, isToday } from 'date-fns'
import { pl } from 'date-fns/locale'
import { toast } from 'sonner'
import { useLiveQuery } from 'dexie-react-hooks'
import { CalendarDays, Loader2, WifiOff } from 'lucide-react'

import Header from '@/components/layout/Header'
import FullScreenSpinner from '@/components/layout/FullScreenSpinner'
import Button from '@/components/ui/Button'
import { ApiHttpError, ApiNetworkError, apiErrorMessage } from '@/lib/api'
import { useSession } from '@/lib/auth'
import { cn } from '@/lib/cn'
import type { PendingEvent } from '@/lib/db'
import { useOnlineStatus } from '@/lib/use-online-status'

import AutoCloseModal, {
  AutoCloseReminder,
} from '@/features/work-sessions/auto-close-modal'
import BigButton, {
  type BigButtonMode,
  type BigButtonStatus,
} from '@/features/work-sessions/big-button'
import {
  SERVER_STATE_MAX_AGE_MS,
  clockAvailability,
  isServerStateFresh,
  type ClockAvailability,
} from '@/features/work-sessions/clock-availability'
import GpsDeniedModal from '@/features/work-sessions/gps-denied-modal'
import { MonthlyProgressCard } from '@/features/work-sessions/monthly-progress-card'
import { discardLiveEvents, getPendingEvents } from '@/features/work-sessions/queue'
import RemoteEndModal, {
  RemoteEndReminder,
} from '@/features/work-sessions/remote-end-modal'
import RodoModal from '@/features/work-sessions/rodo-modal'
import SessionCounter from '@/features/work-sessions/session-counter'
import SessionsList from '@/features/work-sessions/sessions-list'
import { GpsError, useGeolocation } from '@/features/work-sessions/use-geolocation'
import {
  myWorkSessionsQueryKey,
  useEndWorkSession,
  useMyWorkSessions,
  useStartWorkSession,
  type WorkSessionsMeResponse,
} from '@/features/work-sessions/use-work-sessions'

const AVAILABILITY_REASON_ID = 'clock-availability-reason'

/**
 * Po tylu nieudanych próbach zaległego zapisu pokazujemy wyjście awaryjne.
 * Wcześniej to najpewniej chwilowy brak sieci; później blokada przycisku
 * zaczyna kosztować więcej niż ręczne dopisanie sesji przez admina.
 */
const LEGACY_DISCARD_AFTER_ATTEMPTS = 2

function availabilityReason(availability: ClockAvailability): string | null {
  switch (availability.kind) {
    case 'ok':
      return null
    case 'legacy': {
      const when = format(availability.head.occurredAt, 'dd.MM, HH:mm')
      // Po nieudanej próbie „Wysyłam…" kłamało — obok stało już wyjście
      // awaryjne „Pomiń zaległy zapis".
      return availability.head.attempts > 0
        ? `Nie udało się jeszcze wysłać zapisu zachowanego wcześniej na tym urządzeniu (${when}). Kolejna próba nastąpi automatycznie.`
        : `Wysyłam zapis zachowany wcześniej na tym urządzeniu (${when})…`
    }
    case 'offline':
      return 'Brak połączenia z serwerem. Rozpoczęcie i zakończenie pracy wymaga internetu — sprawdź Wi-Fi lub dane komórkowe.'
    case 'checking':
      return 'Sprawdzam połączenie z serwerem…'
  }
}

/**
 * Sama godzina przy sesji sprzed dzisiaj („od 14:20" w poniedziałek o starcie
 * z piątku) ukrywała, że sesja wisi od kilku dni.
 */
function startedLabel(startedAt: string): string {
  const date = new Date(startedAt)
  return isToday(date)
    ? format(date, 'HH:mm')
    : format(date, 'EEE dd.MM, HH:mm', { locale: pl })
}

export default function EmployeeHome() {
  const qc = useQueryClient()
  const session = useSession()
  const sessionsQuery = useMyWorkSessions()
  const geolocation = useGeolocation()
  const startMutation = useStartWorkSession()
  const endMutation = useEndWorkSession()
  const online = useOnlineStatus()
  const [gpsDeniedOpen, setGpsDeniedOpen] = useState(false)
  const [autoCloseDismissed, setAutoCloseDismissed] = useState(false)
  // Odłożenie trzymamy PO ID sesji, nie flagą: „później" odkłada całą BIEŻĄCĄ
  // kolejkę, ale kolejne zakończenie pracy poza placówką (nowe id) ma znowu
  // wyskoczyć modalem.
  const [remoteEndDismissedIds, setRemoteEndDismissedIds] = useState<string[]>([])
  const [remoteEndOpenedFromReminder, setRemoteEndOpenedFromReminder] = useState(false)
  // Cały handler kliknięcia (GPS + kontrola stanu + zapis) jako jedna operacja.
  // Ref blokuje drugie stuknięcie synchronicznie, zanim React przerysuje przycisk.
  const [clickPhase, setClickPhase] = useState<'idle' | 'gps' | 'saving'>('idle')
  const clickInFlight = useRef(false)
  const [discardingLegacy, setDiscardingLegacy] = useState(false)
  const [, forceRender] = useState(0)

  // Zaległe zapisy poprzedniej wersji aplikacji. Ekran ich NIE nakłada na stan
  // z serwera (tak powstała sesja „pracujesz od piątku 14:20" widoczna tylko na
  // telefonie) — służą wyłącznie do zablokowania przycisku, dopóki nie wyjdą.
  // Bez wartości domyślnej: `undefined` = IndexedDB jeszcze nie odpowiedziała.
  const legacyEvents = useLiveQuery(() => getPendingEvents(), [])

  // Przycisk i licznik wynikają WYŁĄCZNIE z `/me`. Start/koniec pracy istnieje
  // dopiero wtedy, gdy potwierdził go serwer.
  const serverState = qc.getQueryState<WorkSessionsMeResponse>(myWorkSessionsQueryKey())
  const now = Date.now()
  const availability = clockAvailability({
    legacy: legacyEvents,
    online,
    server: serverState,
    now,
  })

  // Dane przekraczają próg świeżości bez żadnego zdarzenia zapytania — sam
  // timer musi przerysować ekran, żeby przycisk zgasł na czas.
  const dataUpdatedAt = sessionsQuery.dataUpdatedAt
  useEffect(() => {
    if (!dataUpdatedAt) return
    const msLeft = dataUpdatedAt + SERVER_STATE_MAX_AGE_MS - Date.now()
    if (msLeft <= 0) return
    const handle = setTimeout(() => forceRender((n) => n + 1), msLeft + 100)
    return () => clearTimeout(handle)
  }, [dataUpdatedAt])

  // Po wybudzeniu z tła dane bywają stare, a interwał jeszcze nie odpalił —
  // wtedy sami prosimy o świeży stan zamiast czekać z zablokowanym przyciskiem.
  const refreshStaleState =
    online &&
    sessionsQuery.status === 'success' &&
    sessionsQuery.fetchStatus === 'idle' &&
    !isServerStateFresh(serverState, now)
  const refetchSessions = sessionsQuery.refetch
  useEffect(() => {
    if (refreshStaleState) void refetchSessions()
  }, [refreshStaleState, refetchSessions])

  const recentSessions = useMemo(
    () => sessionsQuery.data?.sessions ?? [],
    [sessionsQuery.data],
  )
  const openSession = sessionsQuery.data?.openSession ?? null
  const unresolvedAutoClosed = useMemo(
    () => sessionsQuery.data?.unresolvedAutoClosed ?? [],
    [sessionsQuery.data],
  )
  const pendingRemoteEnd = useMemo(
    () => sessionsQuery.data?.pendingRemoteEnd ?? [],
    [sessionsQuery.data],
  )

  const mode: BigButtonMode = sessionsQuery.data
    ? openSession
      ? 'end'
      : 'start'
    : 'unknown'
  const mutationPending = startMutation.isPending || endMutation.isPending
  const busyStatus: BigButtonStatus | null =
    clickPhase === 'gps'
      ? 'gps'
      : clickPhase === 'saving' || mutationPending
        ? 'saving'
        : null
  const buttonStatus: BigButtonStatus =
    busyStatus ?? (availability.kind === 'ok' ? 'ready' : 'unavailable')
  // W trakcie własnego zapisu `/me` jest celowo unieważnione — komunikat
  // „sprawdzam połączenie" pod „Zapisuję…" tylko by mylił.
  const reason = busyStatus ? null : availabilityReason(availability)
  const legacyHead = availability.kind === 'legacy' ? availability.head : null
  const canDiscardLegacy =
    legacyHead !== null && legacyHead.attempts >= LEGACY_DISCARD_AFTER_ATTEMPTS

  /** Dostępność w tej chwili, nie z ostatniego renderu — aplikacja mogła spać w tle. */
  function availabilityNow(legacy: PendingEvent[] | undefined): ClockAvailability {
    return clockAvailability({
      legacy,
      online: navigator.onLine,
      server: qc.getQueryState<WorkSessionsMeResponse>(myWorkSessionsQueryKey()),
      now: Date.now(),
    })
  }

  function currentOpenSessionId(): string | null {
    const data = qc.getQueryData<WorkSessionsMeResponse>(myWorkSessionsQueryKey())
    return data?.openSession?.id ?? null
  }

  async function handleClick() {
    if (clickInFlight.current) return
    if (availabilityNow(legacyEvents).kind !== 'ok') {
      // Render mógł pochodzić sprzed zamrożenia aplikacji. Przerysowanie pokaże
      // powód pod przyciskiem i (przy starych danych) zleci odświeżenie.
      forceRender((n) => n + 1)
      toast.info('Najpierw potwierdzam stan pracy z serwerem — spróbuj ponownie za chwilę.')
      return
    }

    // Zamiar i id zdarzenia ustalamy w chwili kliknięcia. Po czekaniu na GPS
    // tryb przycisku z renderu mógłby już nie odpowiadać serwerowi.
    const intendedOpenSessionId = currentOpenSessionId()
    const intent: 'start' | 'end' = intendedOpenSessionId ? 'end' : 'start'
    const clientEventId = crypto.randomUUID()

    clickInFlight.current = true
    setClickPhase('gps')
    try {
      let gps
      try {
        gps = await geolocation.request()
      } catch (err) {
        if (err instanceof GpsError) {
          if (err.kind === 'denied') {
            setGpsDeniedOpen(true)
            return
          }
          if (err.kind === 'unsupported') {
            toast.error('Twoja przeglądarka nie obsługuje GPS — użyj innej.')
            return
          }
          if (err.kind === 'timeout') {
            toast.error('Nie udało się ustalić lokalizacji w czasie 10 s. Spróbuj ponownie.')
            return
          }
          toast.error('Nie udało się ustalić lokalizacji. Sprawdź czy GPS jest włączony.')
          return
        }
        toast.error('Niespodziewany błąd lokalizacji.')
        return
      }

      if (gps.accuracyM > 100) {
        if (intent === 'start') {
          toast.error(
            `Słaby sygnał GPS (±${Math.round(gps.accuracyM)} m). Podejdź bliżej okna i spróbuj ponownie.`,
          )
          return
        }
        toast.warning(
          `Słaby sygnał GPS (±${Math.round(gps.accuracyM)} m). Sesja zostanie oflagowana w panelu admina.`,
        )
      }

      // GPS trwa do 10 s. W tym czasie sesję mógł zamknąć auto-close albo admin,
      // mógł też dojść zaległy zapis — wtedy wysłanie zamiaru z chwili kliknięcia
      // trafiłoby w zły endpoint.
      if (availabilityNow(await getPendingEvents()).kind !== 'ok') {
        forceRender((n) => n + 1)
        toast.info('Stan pracy wymaga ponownego potwierdzenia z serwera. Nic nie zostało zapisane — spróbuj za chwilę.')
        return
      }
      const openNow = currentOpenSessionId()
      const stillMatches =
        intent === 'start' ? openNow === null : openNow === intendedOpenSessionId
      if (!stillMatches) {
        toast.info('Stan pracy zmienił się w międzyczasie. Nic nie zostało zapisane — sprawdź ekran i spróbuj ponownie.')
        return
      }

      setClickPhase('saving')
      const click = {
        clientEventId,
        lat: gps.lat,
        lng: gps.lng,
        accuracyM: gps.accuracyM,
      }
      try {
        if (intent === 'start') {
          await startMutation.mutateAsync(click)
        } else {
          const ended = await endMutation.mutateAsync(click)
          // Koniec pracy w sesji już zamkniętej automatycznie: modal z pytaniem
          // o godzinę wyjścia ma się pokazać, nawet jeśli wcześniej był odłożony.
          if (ended.autoClosed && ended.autoCloseResolvedAt === null) {
            setAutoCloseDismissed(false)
          }
        }
      } catch (err) {
        if (err instanceof ApiNetworkError) {
          // Serwer mógł zapis przyjąć, a odpowiedź zginąć. Przycisk zostaje
          // zablokowany, dopóki `/me` nie powie, jak jest naprawdę.
          toast.error(
            'Serwer nie potwierdził zapisu. Stan pracy odświeży się automatycznie — sprawdź go przed kolejną próbą.',
            { duration: 8_000 },
          )
        } else if (err instanceof ApiHttpError && err.status === 401) {
          toast.error('Logowanie wygasło, kliknięcie nie zostało zapisane. Zaloguj się ponownie.')
        } else {
          toast.error(apiErrorMessage(err))
        }
      }
    } finally {
      clickInFlight.current = false
      setClickPhase('idle')
    }
  }

  async function handleDiscardLegacy() {
    setDiscardingLegacy(true)
    try {
      await discardLiveEvents('discarded_by_user')
      // Stan z serwera sprzed pominięcia nie mówi, co z tym zapisem się stało.
      void qc.invalidateQueries({ queryKey: ['work-sessions'] })
      toast.info(
        'Zaległy zapis pominięty. Zgłoś to administratorowi — doda sesję ręcznie.',
        { duration: 10_000 },
      )
    } catch {
      toast.error('Nie udało się pominąć zaległego zapisu. Spróbuj ponownie.')
    } finally {
      setDiscardingLegacy(false)
    }
  }

  if (session.isLoading) return <FullScreenSpinner />

  const user = session.data
  const needsRodo = user?.rodoSeenAt == null
  // RODO ma pierwszeństwo — to modal blokujący. Auto-close pokazujemy dopiero po nim.
  const pendingAutoClose = unresolvedAutoClosed[0] ?? null
  const showAutoCloseModal =
    pendingAutoClose !== null && !needsRodo && !autoCloseDismissed

  // Pytanie o godzinę wyjścia poza placówką jest trzecie w kolejce. Blokuje je
  // NIEROZSTRZYGNIĘTY auto-close, a nie wyświetlany modal — inaczej „zrobię to
  // później" przy auto-close natychmiast wyrzuca kolejne okno na wierzch.
  // Kliknięcie w baner otwiera modal mimo tej blokady.
  const pendingRemoteEndTarget = remoteEndOpenedFromReminder
    ? (pendingRemoteEnd[0] ?? null)
    : (pendingRemoteEnd.find((s) => !remoteEndDismissedIds.includes(s.id)) ?? null)
  const showRemoteEndModal =
    pendingRemoteEndTarget !== null &&
    !needsRodo &&
    // Twarde wykluczenie: dwa dialogi `aria-modal` na tym samym z-indeksie to
    // ekran nie do przejścia dla czytnika ekranu. Otwarcie z banera omija
    // blokadę „są zaległe auto-close", ale nigdy widocznego modala.
    !showAutoCloseModal &&
    (remoteEndOpenedFromReminder || unresolvedAutoClosed.length === 0)

  // Bez potwierdzenia z serwera ten sam tekst co przy świeżym stanie
  // („Pracujesz od…" obok tykającego licznika) udawał bieżącą prawdę.
  const unconfirmed = availability.kind === 'offline'
  const statusText = !sessionsQuery.data
    ? sessionsQuery.isFetching
      ? 'Ładuję stan pracy…'
      : 'Stan pracy nieznany'
    : unconfirmed
      ? openSession
        ? `Ostatni potwierdzony stan: praca od ${startedLabel(openSession.startedAt)}`
        : 'Ostatni potwierdzony stan: brak otwartej sesji'
      : openSession
        ? `Pracujesz od ${startedLabel(openSession.startedAt)}`
        : 'Brak otwartej sesji'

  return (
    <div className="min-h-[100svh] flex flex-col bg-bg-canvas">
      <Header />
      <main className="flex-1 px-6 py-8 w-full max-w-[480px] mx-auto">
        {unresolvedAutoClosed.length > 0 && !showAutoCloseModal && (
          <div className="mb-4">
            <AutoCloseReminder
              count={unresolvedAutoClosed.length}
              onOpen={() => setAutoCloseDismissed(false)}
            />
          </div>
        )}

        {pendingRemoteEnd.length > 0 && !showRemoteEndModal && (
          <div className="mb-4">
            <RemoteEndReminder
              count={pendingRemoteEnd.length}
              onOpen={() => {
                setRemoteEndDismissedIds([])
                setRemoteEndOpenedFromReminder(true)
              }}
            />
          </div>
        )}

        <p className="text-sm text-text-muted text-center mb-4">{statusText}</p>

        <BigButton
          mode={mode}
          status={buttonStatus}
          describedBy={AVAILABILITY_REASON_ID}
          onClick={() => void handleClick()}
        />

        {/* Region `status` istnieje zawsze — czytnik ekranu ogłasza zmianę
            treści, a nie pojawienie się nowego elementu. Przyciski stoją poza
            nim, żeby ich etykiety nie były odczytywane razem z powodem. */}
        <div
          className={cn(
            reason && 'mt-3 rounded-card border border-border-subtle bg-bg-surface px-4 py-3',
          )}
        >
          <p
            id={AVAILABILITY_REASON_ID}
            role="status"
            aria-live="polite"
            className={cn(reason && 'flex items-start gap-2 text-sm text-text-primary')}
          >
            {reason && (
              <>
                {availability.kind === 'offline' ? (
                  <WifiOff className="h-4 w-4 mt-0.5 flex-shrink-0 text-warning" aria-hidden />
                ) : (
                  <Loader2
                    className="h-4 w-4 mt-0.5 flex-shrink-0 animate-spin text-brand-accent"
                    aria-hidden
                  />
                )}
                <span>{reason}</span>
              </>
            )}
          </p>

          {/* Bez `loading`/`disabled` w trakcie sprawdzania: wyłączony przycisk
              gubi fokus klawiatury, a nieudana próba zostawia go na miejscu. */}
          {reason && availability.kind === 'offline' && (
            <Button
              type="button"
              variant="secondary"
              aria-busy={sessionsQuery.isFetching}
              onClick={() => {
                if (!sessionsQuery.isFetching) void sessionsQuery.refetch()
              }}
              className="mt-3 w-full min-h-[44px]"
            >
              {sessionsQuery.isFetching && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              )}
              Sprawdź ponownie
            </Button>
          )}

          {reason && canDiscardLegacy && (
            <div className="mt-3 pt-3 border-t border-border-subtle">
              <p className="text-xs text-text-muted">
                Ten zapis nie chce się wysłać. Możesz go pominąć — zgłoś to wtedy
                administratorowi, który doda sesję ręcznie.
              </p>
              <button
                type="button"
                onClick={() => void handleDiscardLegacy()}
                disabled={discardingLegacy}
                className="mt-1 min-h-[44px] text-sm text-text-muted underline underline-offset-2 hover:text-text-primary transition disabled:pointer-events-none"
              >
                Pomiń zaległy zapis
              </button>
            </div>
          )}
        </div>

        {openSession && <SessionCounter startedAt={openSession.startedAt} />}

        <h2 className="mt-10 mb-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
          Ostatnie sesje
        </h2>
        {sessionsQuery.isLoading ? (
          <p className="text-center text-sm text-text-faint py-4">Ładuję…</p>
        ) : !sessionsQuery.data ? (
          // Po nieudanym pierwszym pobraniu pusta lista udawałaby „brak sesji".
          <p className="text-center text-sm text-text-muted py-4">
            Lista sesji pojawi się po połączeniu z serwerem.
          </p>
        ) : (
          <SessionsList sessions={recentSessions} />
        )}

        <div className="mt-8 pt-4 border-t border-border-subtle text-center">
          <Link
            to="/vacation"
            className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-primary transition"
          >
            <CalendarDays className="h-4 w-4" aria-hidden />
            Mój urlop
            <span aria-hidden>→</span>
          </Link>
        </div>

        <div className="mt-6">
          <MonthlyProgressCard />
        </div>
      </main>

      {needsRodo && <RodoModal />}

      {showAutoCloseModal && pendingAutoClose && (
        <AutoCloseModal
          key={pendingAutoClose.id}
          session={pendingAutoClose}
          onResolved={() => {
            void sessionsQuery.refetch()
          }}
          onDismiss={() => setAutoCloseDismissed(true)}
        />
      )}

      {showRemoteEndModal && pendingRemoteEndTarget && (
        <RemoteEndModal
          key={pendingRemoteEndTarget.id}
          session={pendingRemoteEndTarget}
          onResolved={() => {
            // Flaga opisuje JEDNO otwarcie z banera; gdyby przeżyła zapis,
            // kolejne pytanie omijałoby priorytet auto-close do końca sesji.
            setRemoteEndOpenedFromReminder(false)
            void sessionsQuery.refetch()
          }}
          onDismiss={() => {
            setRemoteEndOpenedFromReminder(false)
            // Odkładamy CAŁĄ kolejkę, nie samo widoczne pytanie: dopisanie
            // jednego id natychmiast podstawiało kolejną zaległą sesję w to
            // samo miejsce, więc pracownik z trzema zaległościami stukał
            // „później" trzy razy, zanim odsłonił przycisk startu — wyglądało
            // to jak zapętlony ekran. Baner z licznikiem i tak zostaje.
            setRemoteEndDismissedIds((ids) => [
              ...ids,
              ...pendingRemoteEnd
                .map((s) => s.id)
                .filter((id) => !ids.includes(id)),
            ])
          }}
        />
      )}

      {gpsDeniedOpen && (
        <GpsDeniedModal
          onClose={() => setGpsDeniedOpen(false)}
          onRetry={() => {
            setGpsDeniedOpen(false)
            // Ponowienie ustala zamiar od nowa — stan mógł się zmienić, gdy
            // modal był otwarty.
            void handleClick()
          }}
        />
      )}
    </div>
  )
}
